import { useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { getUserFirstName } from '@/lib/supabase'
import { CreateLinkModal } from '@/components/CreateLinkModal'
import { EmailVerificationBanner } from '@/components/EmailVerificationBanner'
import { LogoMark } from '@/components/Logo'

interface LayoutProps {
  children: ReactNode
  title?: string
  subtitle?: string
}

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
  { path: '/links', label: 'Links', icon: 'link' },
  { path: '/bio-pages', label: 'Bio Pages', icon: 'web' },
  { path: '/analytics', label: 'Analytics', icon: 'analytics' },
  { path: '/instagram', label: 'Instagram', icon: 'smart_toy' },
  { path: '/account', label: 'Settings', icon: 'settings' },
]

function getSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem('sidebar-collapsed') === 'true'
  } catch {
    return false
  }
}

export function Layout({ children, title, subtitle }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, signOut } = useAuthStore()
  const { theme, setTheme } = useThemeStore()
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(getSidebarCollapsed)

  const toggleCollapsed = () => {
    const next = !collapsed
    setCollapsed(next)
    try { localStorage.setItem('sidebar-collapsed', String(next)) } catch { /* ignore storage errors */ }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  const userInitial = user?.email?.charAt(0).toUpperCase() || 'U'
  const userName = getUserFirstName(user)
  const userEmail = user?.email || 'user@example.com'

  return (
    <div className="flex h-screen overflow-hidden bg-[#f6f6f8] dark:bg-[#101622]">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar Navigation */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 border-r border-[#e7ebf3] dark:border-white/10 bg-white dark:bg-[#0d121b] flex flex-col justify-between
        transform transition-all duration-200 ease-in-out
        lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${collapsed ? 'lg:w-[72px]' : 'lg:w-64'} w-64
      `}>
        <div className={`flex flex-col gap-8 ${collapsed ? 'lg:p-3 p-6' : 'p-6'}`}>
          {/* Logo/Workspace */}
          <div className="flex items-center gap-2.5">
            <div className="shrink-0">
              <LogoMark size={32} />
            </div>
            <div className={`flex flex-col overflow-hidden transition-all duration-200 ${collapsed ? 'lg:w-0 lg:opacity-0' : 'w-auto opacity-100'}`}>
              <h1 className="text-[#0d121b] dark:text-white text-sm font-bold tracking-tight leading-tight whitespace-nowrap">URLzippy</h1>
              <p className="text-[#4c669a] dark:text-gray-400 text-xs font-normal whitespace-nowrap">Free Plan</p>
            </div>
          </div>

          {/* Nav Items */}
          <nav className="flex flex-col gap-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                title={collapsed ? item.label : undefined}
                className={`
                  flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                  ${collapsed ? 'lg:justify-center' : ''}
                  ${location.pathname === item.path
                    ? 'bg-[#1754cf]/10 text-[#1754cf]'
                    : 'text-[#4c669a] dark:text-gray-400 hover:bg-[#e7ebf3] dark:hover:bg-white/5'
                  }
                `}
              >
                <span className="material-symbols-outlined shrink-0">{item.icon}</span>
                <span className={`text-sm font-medium transition-all duration-200 ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        {/* Collapse toggle - desktop only */}
        <button
          onClick={toggleCollapsed}
          className="hidden lg:flex absolute -right-3 top-7 z-50 w-6 h-6 rounded-full bg-white dark:bg-[#1c2230] border border-[#e7ebf3] dark:border-white/10 items-center justify-center text-[#4c669a] hover:text-[#0d121b] dark:hover:text-white shadow-sm transition-colors"
        >
          <span className={`material-symbols-outlined text-[14px] transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`}>
            chevron_left
          </span>
        </button>

        {/* Feedback Action */}
        <div className={`mt-auto px-6 pb-4 ${collapsed ? 'lg:px-3 lg:pb-2' : ''}`}>
          <Link
            to="/feedback"
            onClick={() => setSidebarOpen(false)}
            className={`w-full bg-[#f6f6f8] dark:bg-[#1c2230] text-[#0d121b] dark:text-white border border-[#e7ebf3] dark:border-white/10 text-sm font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-[#e7ebf3] dark:hover:bg-white/5 transition-colors shadow-sm`}
            title={collapsed ? 'Submit Feedback' : undefined}
          >
            <span className="material-symbols-outlined shrink-0 text-[18px]">rate_review</span>
            <span className={`transition-all duration-200 ${collapsed ? 'lg:hidden' : ''}`}>Submit Feedback</span>
          </Link>
        </div>

        {/* Bottom Action */}
        <div className={`border-t border-[#e7ebf3] dark:border-white/10 ${collapsed ? 'lg:p-3 p-6' : 'p-6'}`}>
          <button
            onClick={() => setCreateModalOpen(true)}
            className={`w-full bg-[#1754cf] text-white text-sm font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 shadow-[0_4px_14px_0_rgba(23,84,207,0.39)] hover:opacity-90 transition-opacity`}
            title={collapsed ? 'Create link' : undefined}
          >
            <span className="material-symbols-outlined shrink-0">add</span>
            <span className={`transition-all duration-200 ${collapsed ? 'lg:hidden' : ''}`}>Create link</span>
          </button>
          <div className={`flex items-center gap-3 mt-4 px-2 py-2 ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}>
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#1754cf] to-blue-400 flex items-center justify-center text-[10px] text-white font-bold shrink-0">
              {userInitial}
            </div>
            <div className={`flex-1 overflow-hidden transition-all duration-200 ${collapsed ? 'lg:hidden' : ''}`}>
              <p className="text-xs font-medium truncate text-[#0d121b] dark:text-white">{userName}</p>
              <p className="text-[10px] text-gray-500 truncate">{userEmail}</p>
            </div>
            <button onClick={handleSignOut} className={`text-gray-400 hover:text-gray-600 ${collapsed ? 'lg:hidden' : ''}`}>
              <span className="material-symbols-outlined text-base">logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-y-auto">
        <EmailVerificationBanner />
        {/* Header */}
        <header className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-[#e7ebf3] dark:border-white/10 bg-white/50 dark:bg-[#0d121b]/50 backdrop-blur-md sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-1 text-[#4c669a] hover:text-[#0d121b] dark:hover:text-white"
            >
              <span className="material-symbols-outlined">menu</span>
            </button>
            <span className="text-[#4c669a] dark:text-gray-400 text-sm hidden sm:inline">Workspace</span>
            <span className="text-[#cfd7e7] dark:text-gray-600">/</span>
            <span className="text-[#0d121b] dark:text-white text-sm font-medium">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative hidden sm:block">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="material-symbols-outlined text-[#4c669a] dark:text-gray-400 text-base">search</span>
              </div>
              <input
                className="pl-10 pr-4 py-1.5 bg-[#e7ebf3] dark:bg-white/5 border-none rounded-lg text-sm w-48 md:w-64 focus:ring-1 focus:ring-[#1754cf] text-[#0d121b] dark:text-white placeholder:text-[#4c669a]"
                placeholder="Search links..."
                type="text"
              />
            </div>
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark')}
              className="p-2 rounded-lg text-[#4c669a] dark:text-gray-400 hover:bg-[#e7ebf3] dark:hover:bg-white/5 transition-colors"
              title={`Theme: ${theme}`}
            >
              <span className="material-symbols-outlined text-[20px]">
                {theme === 'dark' ? 'dark_mode' : theme === 'light' ? 'light_mode' : 'contrast'}
              </span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-8 max-w-7xl mx-auto w-full">
          {(title || subtitle) && (
            <div className="flex flex-wrap justify-between items-end gap-3 mb-8">
              <div>
                {title && (
                  <h2 className="text-[#0d121b] dark:text-white text-3xl font-black tracking-tight">{title}</h2>
                )}
                {subtitle && (
                  <p className="text-[#4c669a] dark:text-gray-400 text-sm mt-1">{subtitle}</p>
                )}
              </div>
            </div>
          )}
          {children}
        </div>
      </main>

      {/* Create Link Modal */}
      <CreateLinkModal isOpen={createModalOpen} onClose={() => setCreateModalOpen(false)} />
    </div>
  )
}
